// store#plans — the pricing page and the two dialogs that sell from it.
//
// Card state is two values: the billing duration (monthly/yearly) and, on a
// card that spans several plans, which one its slider sits on. Both come from
// the server pre-priced (data-plan-tiers), so nothing is recalculated here.
//
// Clicking a card's button opens one of two modals:
//
//   no subscription yet → the purchase modal, which mounts Stripe's card form
//                         against a SetupIntent and then creates the
//                         subscription from the saved card.
//   already subscribed  → the change modal, which only has to explain what
//                         the switch does (prorated, or a cancellation that
//                         runs to the end of the period) and get a yes.
//
// While DISABLE_NEW_PLANS is set, anything that would start or grow a
// subscription opens the beta wall instead (see beta_modal.ts). Downgrades and
// cancellations still go through, so nobody already paying is stuck on a plan.
import { loadStripe, type Stripe, type StripeElements } from "@stripe/stripe-js"
import { openBetaGate } from "../beta_modal"
import { registerPage } from "../first"

type Duration = "monthly" | "yearly"

type PlanTier = {
  key: string
  name: string
  monthly: number | null
  annual: number | null
  usage: string | null
  workspaces: string | null
  free: boolean
}

let duration: Duration = "monthly"
const positions = new WeakMap<HTMLElement, number>()

let stripe: Stripe | null = null
let elements: StripeElements | null = null
let pendingTier: PlanTier | null = null

function root(): HTMLElement | null {
  return document.querySelector<HTMLElement>("[data-plans-root]")
}

function billingInterval(): "month" | "year" {
  return duration === "yearly" ? "year" : "month"
}

function currentPlanKey(): string {
  return root()?.dataset.currentPlan ?? ""
}

function isSubscribed(): boolean {
  return root()?.dataset.subscribed === "true"
}

function tiersFor(card: HTMLElement): PlanTier[] {
  try {
    const parsed = JSON.parse(card.dataset.planTiers ?? "[]") as PlanTier[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function currentTier(card: HTMLElement): PlanTier | null {
  const tiers = tiersFor(card)
  if (tiers.length === 0) return null

  const position = positions.get(card) ?? 1
  return tiers[Math.min(Math.max(position, 1), tiers.length) - 1] ?? null
}

function priceFor(tier: PlanTier): number | null {
  return duration === "yearly" ? tier.annual : tier.monthly
}

function money(dollars: number): string {
  return `$${dollars.toLocaleString()}`
}

// ---------- Rendering ----------

function render(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-plans-duration] [data-duration]").forEach((button) => {
    button.classList.toggle("is-selected", button.dataset.duration === duration)
  })

  document.querySelectorAll<HTMLElement>("[data-plan-card]").forEach((card) => {
    const tier = currentTier(card)
    if (!tier) return

    const name = card.querySelector<HTMLElement>("[data-plan-name]")
    if (name) name.textContent = tier.name

    const price = card.querySelector<HTMLElement>("[data-plan-price]")
    const dollars = priceFor(tier)
    if (price && dollars !== null) price.textContent = money(dollars)

    const discount = card.querySelector<HTMLElement>("[data-plan-discount]")
    if (discount) discount.hidden = duration === "monthly"

    const usage = card.querySelector<HTMLElement>("[data-plan-usage]")
    if (usage && tier.usage) usage.textContent = tier.usage

    const workspaces = card.querySelector<HTMLElement>("[data-plan-workspaces]")
    if (workspaces && tier.workspaces) workspaces.textContent = tier.workspaces

    // On the plan you already hold, the button gives way to a plain label.
    const onThisPlan = tier.key === currentPlanKey()
    const action = card.querySelector<HTMLButtonElement>("[data-plan-action]")
    const current = card.querySelector<HTMLElement>("[data-plan-current]")
    if (action) {
      action.hidden = onThisPlan
      action.dataset.planKey = tier.key
      action.textContent = actionLabel(tier)
    }
    if (current) current.hidden = !onThisPlan
  })
}

function actionLabel(tier: PlanTier): string {
  if (!isSubscribed()) return "Upgrade"

  const currentCard = [...document.querySelectorAll<HTMLElement>("[data-plan-card]")]
    .find((card) => tiersFor(card).some((t) => t.key === currentPlanKey()))
  const held = currentCard && tiersFor(currentCard).find((t) => t.key === currentPlanKey())
  const heldPrice = held?.monthly ?? 0
  return (tier.monthly ?? 0) < heldPrice ? "Downgrade" : "Upgrade"
}

// ---------- Modal plumbing ----------

function modal(selector: string): HTMLDialogElement | null {
  return document.querySelector<HTMLDialogElement>(selector)
}

function openModal(dialog: HTMLDialogElement | null): void {
  if (!dialog) return
  showError(dialog, null)
  dialog.showModal()
}

function showError(dialog: HTMLDialogElement, message: string | null): void {
  const box = dialog.querySelector<HTMLElement>("[data-modal-error]")
  if (!box) return
  box.textContent = message ?? ""
  box.hidden = !message
}

async function postJson(url: string, body: unknown): Promise<any> {
  const token = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "X-CSRF-Token": token } : {}),
    },
    body: JSON.stringify(body),
  })

  const payload = await response.json().catch(() => ({}))
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || "Something went wrong. Please try again.")
  }
  return payload
}

// ---------- Purchase (first subscription) ----------

async function openPurchase(tier: PlanTier): Promise<void> {
  const dialog = modal("[data-purchase-modal]")
  const settings = root()
  if (!dialog || !settings) return

  pendingTier = tier
  const nameSlot = dialog.querySelector<HTMLElement>("[data-purchase-plan-name]")
  if (nameSlot) nameSlot.textContent = tier.name

  const submit = dialog.querySelector<HTMLButtonElement>("[data-purchase-submit]")
  const loading = dialog.querySelector<HTMLElement>("[data-purchase-loading]")
  const mount = dialog.querySelector<HTMLElement>("[data-purchase-payment-element]")
  if (submit) submit.disabled = true
  if (loading) loading.hidden = false
  if (mount) mount.innerHTML = ""
  openModal(dialog)

  try {
    const payload = await postJson(settings.dataset.paymentIntentUrl ?? "", {
      plan: tier.key,
      interval: billingInterval(),
    })

    const summary = dialog.querySelector<HTMLElement>("[data-purchase-summary]")
    if (summary) summary.textContent = purchaseSummary(payload.summary)

    stripe = stripe ?? (await loadStripe(settings.dataset.stripePublishableKey ?? ""))
    if (!stripe || !mount) throw new Error("Stripe couldn't be loaded.")

    elements = stripe.elements({
      clientSecret: payload.setup_intent_client_secret,
      appearance: { theme: "stripe", variables: { colorPrimary: "#155a96" } },
    })
    elements.create("payment").mount(mount)

    if (loading) loading.hidden = true
    if (submit) submit.disabled = false
  } catch (error) {
    if (loading) loading.hidden = true
    showError(dialog, error instanceof Error ? error.message : String(error))
  }
}

type PurchaseSummary = {
  name: string
  interval: string
  total_cents: number
  coupon?: { code: string; name: string; percent_off: number; total_cents_after_discount: number }
}

function purchaseSummary(summary: PurchaseSummary): string {
  const period = summary.interval === "year" ? "annually" : "per month"
  const list = money(Math.round(summary.total_cents / 100))

  if (!summary.coupon) {
    return `${summary.name}, ${list} ${period}.`
  }

  // Stripe applies the real discount; this is the buyer-facing preview.
  const discounted = money(Math.round(summary.coupon.total_cents_after_discount / 100))
  return `${summary.name}, billed ${discounted} ${period} with your ${summary.coupon.percent_off}% off coupon (normally ${list}).`
}

async function submitPurchase(event: Event): Promise<void> {
  event.preventDefault()
  const dialog = modal("[data-purchase-modal]")
  const settings = root()
  if (!dialog || !settings || !stripe || !elements || !pendingTier) return

  const submit = dialog.querySelector<HTMLButtonElement>("[data-purchase-submit]")
  if (submit) submit.disabled = true
  showError(dialog, null)

  try {
    // The card is saved rather than charged here; the subscription we create
    // next is what bills it.
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    })
    if (error) throw new Error(error.message || "That card couldn't be saved.")
    if (!setupIntent?.id) throw new Error("Stripe didn't return a saved card.")

    await postJson(settings.dataset.subscribeUrl ?? "", {
      plan: pendingTier.key,
      interval: billingInterval(),
      setup_intent_id: setupIntent.id,
    })

    window.location.reload()
  } catch (error) {
    showError(dialog, error instanceof Error ? error.message : String(error))
    if (submit) submit.disabled = false
  }
}

// ---------- Change (already subscribed) ----------

function openChange(tier: PlanTier): void {
  const dialog = modal("[data-change-modal]")
  if (!dialog) return

  pendingTier = tier
  const downgrade = actionLabel(tier) === "Downgrade"
  const title = dialog.querySelector<HTMLElement>("[data-change-title]")
  const summary = dialog.querySelector<HTMLElement>("[data-change-summary]")
  const detail = dialog.querySelector<HTMLElement>("[data-change-detail]")
  const confirm = dialog.querySelector<HTMLButtonElement>("[data-change-confirm]")

  if (tier.free) {
    if (title) title.textContent = "Cancel your subscription"
    if (summary) summary.textContent = "Your plan will run to the end of the period you've already paid for, then move to Free."
    if (detail) detail.textContent = "You keep everything you've published, and you can pick a plan again at any time."
    if (confirm) confirm.textContent = "Cancel my plan"
  } else {
    const dollars = priceFor(tier)
    if (title) title.textContent = `${downgrade ? "Downgrade" : "Upgrade"} to ${tier.name}`
    if (summary) {
      summary.textContent = dollars === null
        ? `Switch to ${tier.name}.`
        : `Switch to ${tier.name} at ${money(dollars)} ${duration === "yearly" ? "a month, billed annually" : "a month"}.`
    }
    if (detail) {
      detail.textContent = downgrade
        ? "The difference is credited against your next invoice, and the smaller plan starts now."
        : "You'll be charged the prorated difference today, and the bigger plan starts immediately."
    }
    if (confirm) confirm.textContent = downgrade ? "Downgrade" : "Upgrade"
  }

  if (confirm) confirm.disabled = false
  openModal(dialog)
}

async function submitChange(): Promise<void> {
  const dialog = modal("[data-change-modal]")
  const settings = root()
  if (!dialog || !settings || !pendingTier) return

  const confirm = dialog.querySelector<HTMLButtonElement>("[data-change-confirm]")
  if (confirm) confirm.disabled = true
  showError(dialog, null)

  try {
    await postJson(settings.dataset.changeUrl ?? "", {
      plan: pendingTier.key,
      interval: billingInterval(),
    })
    window.location.reload()
  } catch (error) {
    showError(dialog, error instanceof Error ? error.message : String(error))
    if (confirm) confirm.disabled = false
  }
}

// ---------- Wiring ----------

export const StorePlansPage = {
  onDomReady(): void {
    const settings = root()
    if (!settings) return

    document.querySelector("[data-plans-duration]")?.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-duration]")
      const selected = button?.dataset.duration
      if (selected === "monthly" || selected === "yearly") {
        duration = selected
        render()
      }
    })

    document.querySelectorAll<HTMLInputElement>("input[data-plan-tier-slider]").forEach((slider) => {
      const card = slider.closest<HTMLElement>("[data-plan-card]")
      if (!card) return

      const initial = Number.parseInt(slider.value, 10)
      positions.set(card, Number.isFinite(initial) && initial >= 1 ? initial : 1)

      slider.addEventListener("input", () => {
        const selected = Number.parseInt(slider.value, 10)
        if (Number.isFinite(selected) && selected >= 1) {
          positions.set(card, selected)
          render()
        }
      })
    })

    document.querySelectorAll<HTMLButtonElement>("[data-plan-action]").forEach((button) => {
      button.addEventListener("click", () => {
        const card = button.closest<HTMLElement>("[data-plan-card]")
        const tier = card && currentTier(card)
        if (!tier) return

        // Closed for the beta — but only in the direction that signs someone
        // up for more. Leaving a plan is always allowed.
        if (actionLabel(tier) === "Upgrade" && openBetaGate()) return

        if (isSubscribed()) openChange(tier)
        else void openPurchase(tier)
      })
    })

    document.querySelectorAll<HTMLElement>("[data-modal-close]").forEach((button) => {
      button.addEventListener("click", () => button.closest("dialog")?.close())
    })

    document.querySelector("[data-purchase-form]")?.addEventListener("submit", (event) => {
      void submitPurchase(event)
    })

    document.querySelector("[data-change-confirm]")?.addEventListener("click", () => {
      void submitChange()
    })

    render()
  },
}

registerPage("StorePlansPage", StorePlansPage)
